import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AiAnalysisView from "@/components/AiAnalysisView";
import ImagingCompareDialog from "@/components/ImagingCompareDialog";
import { Plus, Loader2, Trash2, ScanLine, UploadCloud, FileText, Download } from "lucide-react";

const EXAM_TYPES = [
  "Radiografia de Tórax", "Radiografia Óssea", "Tomografia", "Ressonância Magnética",
  "Ultrassonografia", "Mamografia", "Densitometria", "Laudo em PDF", "Outro",
];

export default function ImagingTab({ patientId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [examType, setExamType] = useState("");
  const [examDate, setExamDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();

  const load = () => api.get(`/patients/${patientId}/imaging`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [patientId]);

  const compressImage = (f) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(f);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const maxDim = 1600;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("falha ao comprimir"));
            const base = (f.name || "imagem").replace(/\.[^.]+$/, "");
            resolve(new File([blob], `${base}.jpg`, { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.82
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("imagem inválida")); };
      img.src = url;
    });

  const onFile = async (f) => {
    if (!f) return;
    const pdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    const img = ["image/jpeg", "image/png", "image/webp"].includes(f.type);
    if (!pdf && !img) { toast.error("Use PDF, JPG, PNG ou WEBP"); return; }
    if (pdf) {
      if (f.size > 15 * 1024 * 1024) { toast.error("PDF muito grande (máx 15MB)"); resetInput(); return; }
      setFile(f);
      setIsPdf(true);
      setPreview("");
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 30MB). Reduza a resolução e tente novamente.");
      resetInput();
      return;
    }
    try {
      const useFile = f.size < 800 * 1024 ? f : await compressImage(f);
      setFile(useFile);
      setIsPdf(false);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(useFile);
    } catch (e) {
      toast.error("Não foi possível processar a imagem (muito grande?). Tente uma menor.");
    } finally {
      resetInput();
    }
  };

  const resetInput = () => { if (inputRef.current) inputRef.current.value = ""; };

  const save = async () => {
    if (!examType) { toast.error("Selecione o tipo de exame"); return; }
    if (!file) { toast.error("Envie uma imagem ou PDF"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("exam_type", examType);
      fd.append("exam_date", examDate);
      fd.append("notes", notes || "");
      fd.append("file", file);
      await api.post(`/patients/${patientId}/imaging/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Exame analisado pela IA");
      setOpen(false); setExamType(""); setNotes(""); setFile(null); setPreview(""); setIsPdf(false);
      load();
    } catch (e) {
      const status = e.response?.status;
      if (status === 413) {
        toast.error("Arquivo muito grande para envio. Tente uma imagem menor ou um PDF mais leve.");
      } else if (!e.response) {
        toast.error("Falha no envio do arquivo. Verifique a conexão e o tamanho do arquivo.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally { setSaving(false); }
  };

  const remove = async (iid) => {
    await api.delete(`/patients/${patientId}/imaging/${iid}`);
    load(); toast.success("Exame removido");
  };

  const downloadPdf = (it) => {
    const a = document.createElement("a");
    a.href = `data:application/pdf;base64,${it.pdf_base64}`;
    a.download = it.file_name || "exame.pdf";
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl font-medium">Radiografias, imagens e laudos (PDF)</h3>
        <div className="flex gap-2">
          <ImagingCompareDialog patientId={patientId} items={items} />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-imaging-btn"><Plus className="w-4 h-4 mr-2" /> Enviar arquivo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="imaging-dialog">
            <DialogHeader>
              <DialogTitle>Enviar exame de imagem ou laudo</DialogTitle>
              <DialogDescription>Aceita imagem (JPG, PNG, WEBP) ou arquivo PDF. A IA analisa por visão.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={examType} onValueChange={setExamType}>
                    <SelectTrigger data-testid="imaging-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {EXAM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} data-testid="imaging-date-input" />
                </div>
              </div>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors duration-200"
                data-testid="imaging-dropzone"
              >
                {preview ? (
                  <img src={preview} alt="preview" className="max-h-48 mx-auto rounded-md" />
                ) : isPdf && file ? (
                  <div className="text-primary">
                    <FileText className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">PDF pronto para análise</p>
                  </div>
                ) : (
                  <div className="text-muted-foreground">
                    <UploadCloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Arraste ou clique para enviar (PDF, JPG, PNG, WEBP)</p>
                  </div>
                )}
                <input ref={inputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" hidden
                  onChange={(e) => onFile(e.target.files[0])} data-testid="imaging-file-input" />
              </div>
              <div className="space-y-2">
                <Label>Observações clínicas</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="imaging-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-imaging-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Enviar e analisar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          <ScanLine className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Nenhum arquivo enviado.
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <Card key={it.id} className="p-6 border-border shadow-none" data-testid={`imaging-${it.id}`}>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-64 shrink-0">
                  <img src={it.image_base64 ? `data:${it.mime_type};base64,${it.image_base64}` : it.image_url} alt={it.exam_type}
                    className="w-full rounded-md border border-border bg-black object-contain max-h-64" />
                  {it.source_type === "pdf" && (
                    <div className="mt-2 flex items-center justify-between">
                      <Badge variant="outline" className="border-primary/30 text-primary">
                        <FileText className="w-3 h-3 mr-1" /> PDF{it.page_count ? ` · ${it.page_count} pág.` : ""}
                      </Badge>
                      {it.pdf_base64 && (
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(it)} data-testid={`download-pdf-${it.id}`}>
                          <Download className="w-4 h-4 mr-1" /> Baixar
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-heading text-lg font-medium">{it.exam_type}</h4>
                      <p className="text-sm text-muted-foreground">{it.exam_date}</p>
                      {it.notes && <p className="text-sm mt-1">{it.notes}</p>}
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-imaging-${it.id}`}>
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                  {it.ai_analysis && <AiAnalysisView analysis={it.ai_analysis} />}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
